import { defineStore } from 'pinia'
import { onMounted, ref } from 'vue'
import { supabase } from '@/supabase'
// Substitua round por Math.round, para evitar dependência extra
import { includes } from 'lodash'

export const useDesempenhoGeralStore = defineStore('desempenho', () => {
    const loading = ref(false)
    const dataAtual = new Date()
    const ano = dataAtual.getFullYear()

    // Armazenam as listas completas retornadas do supabase
    const estudantes = ref([])
    const notas = ref([])          // Armazena TODAS as notas
    const notacoes = ref([])       // Armazena TODAS as notações

    // Dicionários de acesso rápido:
    const notasPorEstudante = {}
    const notacoesPorEstudante = {}

    // Resultado final (cálculos)
    const dadosCalculados = ref([])

    // =====================
    // 1) Carregar dados do supabase
    // =====================
    async function loadNotas() {
        const { data, error } = await supabase
            .from('planos_notas')
            .select('*')
            .gte('created_at', `${ano}-01-01T00:00:00Z`)
            .lte('created_at', `${ano}-12-31T23:59:59Z`)

        if (error) {
            console.error(error)
            return
        }
        notas.value = data
    }

    async function loadEstudantes() {
        const { data, error } = await supabase
            .from('users')
            .select('id, nome, img')
            .eq('funcao', 'estudante')

        if (error) {
            console.error(error)
            return
        }
        estudantes.value = data
    }

    async function loadNotacoes() {
        const { data, error } = await supabase
            .from('notacoes')
            .select('*')
            .gte('created_at', `${ano}-01-01T00:00:00Z`)
            .lte('created_at', `${ano}-12-31T23:59:59Z`)

        if (error) {
            console.error(error)
            return
        }
        notacoes.value = data
    }

    // =====================
    // 2) Montar indexação (agrupamento)
    // =====================
    function agruparDadosPorEstudante() {
        // Limpa dicionários caso a função seja chamada mais de uma vez
        for (const key in notasPorEstudante) delete notasPorEstudante[key]
        for (const key in notacoesPorEstudante) delete notacoesPorEstudante[key]

        // Constrói objeto: { [idEstudante]: [ lista de notas ] }
        notas.value.forEach((nota) => {
            if (!notasPorEstudante[nota.estudante]) {
                notasPorEstudante[nota.estudante] = []
            }
            notasPorEstudante[nota.estudante].push(nota)
        })

        // Constrói objeto: { [idEstudante]: [ lista de notações ] }
        notacoes.value.forEach((notacao) => {
            if (!notacoesPorEstudante[notacao.estudante]) {
                notacoesPorEstudante[notacao.estudante] = []
            }
            notacoesPorEstudante[notacao.estudante].push(notacao)
        })
    }

    // =====================
    // 3) Calcular tudo
    // =====================
    function calcularTudo() {
        const dados = []

        // Agora, para cada estudante, acessamos seus dados sem filtrar
        estudantes.value.forEach((estudante) => {
            const idEstudante = estudante.id
            const arrayNotas = notasPorEstudante[idEstudante] || []
            const arrayNotacoes = notacoesPorEstudante[idEstudante] || []

            // Monta o objeto final para cada aluno
            dados.push({
                id: estudante.id,
                nome: estudante.nome,
                img: estudante.img,
                totalAvaliacoes: arrayNotas.length,
                media: calcularMediaPorAluno(arrayNotas),
                basicos: calcularNumeroBasicos(arrayNotacoes),
                disciplinasComBasico: disciplinasComBasico(arrayNotacoes),

                // Exemplo de cálculo mensal:
                fevereiro: calcularMediaPorMes(arrayNotas, 2),
                marco: calcularMediaPorMes(arrayNotas, 3),
                abril: calcularMediaPorMes(arrayNotas, 4),
                maio: calcularMediaPorMes(arrayNotas, 5),
                junho: calcularMediaPorMes(arrayNotas, 6),
                julho: calcularMediaPorMes(arrayNotas, 7),
                agosto: calcularMediaPorMes(arrayNotas, 8),
                setembro: calcularMediaPorMes(arrayNotas, 9),
                outubro: calcularMediaPorMes(arrayNotas, 10),
                novembro: calcularMediaPorMes(arrayNotas, 11)
            })
        })

        dadosCalculados.value = dados
        console.log(dados)
    }

    // =====================
    // 4) Funções auxiliares
    // =====================

    function calcularMediaPorAluno(avaliacoes) {
        if (!avaliacoes.length) return 0

        let soma = 0
        let numeroNotas = 0

        for (const avaliacao of avaliacoes) {
            if (!avaliacao || typeof avaliacao.nota !== 'number') continue
            if (avaliacao.nota > 1 && avaliacao.nota <= 200) {
                soma += avaliacao.nota
                numeroNotas++
            }
        }

        if (numeroNotas > 1) {
            return Math.round(soma / numeroNotas)
        } else {
            return soma // Se tiver só 1 nota válida
        }
    }

    function calcularNumeroBasicos(notacoesArray) {
        let notacoesBasicas = 0
        for (const notacao of notacoesArray) {
            if (notacao.notacao < 3) {
                notacoesBasicas++
            }
        }
        return notacoesBasicas
    }

    function disciplinasComBasico(notacoesArray) {
        const disciplinasComBasico = new Set()
        for (const item of notacoesArray) {
            if (item.notacao < 3) {
                disciplinasComBasico.add(item.disciplina)
            }
        }
        // Retorna quantas disciplinas são < 3
        return disciplinasComBasico.size
    }

    // Exemplo de função para media por mes
    function calcularMediaPorMes(avaliacoes, mesSelecionado) {
        const filtradas = []
        for (const avaliacao of avaliacoes) {
            const mes = new Date(avaliacao.created_at).getMonth() + 1
            if (mes === mesSelecionado) {
                filtradas.push(avaliacao)
            }
        }
        return calcularMediaPorAluno(filtradas)
    }

    // =====================
    // onMounted -> Carrega tudo
    // =====================
    onMounted(async () => {
        loading.value = true

        // Carregamos tudo
        await loadNotas()
        await loadEstudantes()
        await loadNotacoes()

        // Agrupamos os dados por estudante
        agruparDadosPorEstudante()

        // Calculamos estatísticas
        calcularTudo()

        loading.value = false
    })

    return {
        dadosCalculados,
        loading
    }
})
